/**
 * src/PaidUtilitiesPage.js
 *
 * PAID VISINVOKE UTILITIES - dedicated commercial catalogue page.
 *
 * Destination page for the "Paid VISINVOKE Utilities" collection card on the
 * Utilities landing page (/dashboard/utilities). It is the paid counterpart of
 * the Free Utilities catalogue: compact, consistent product cards that scale as
 * more paid utilities are added, instead of one long wall of full product
 * presentations.
 *
 * Catalogue order is commercial hierarchy - highest primary monthly price
 * first (PAID_UTILITIES in UtilitiesPage.js is already in that order).
 *
 * Access model:
 *   - Nothing here grants access. Service Command Centre reads the REAL
 *     service_command_centre_access entitlement from the signed-in user and
 *     either launches the existing in-app route or starts the existing backend
 *     Stripe checkout. Products with no release yet stay Coming Soon and
 *     disabled. No frontend-only ownership state is invented.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ProductNav from './components/ProductNav';
import MoreInfoPill from './components/MoreInfoPill';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { hexA } from './RegistryComponents';
import { AvailabilityBadge, ProductPriceBlock } from './components/productPrimitives';
import { useContentPlannerLaunch } from './config/contentPlannerLaunch';
import { useWebsiteCaptureLaunch, startWebsiteCaptureCheckout } from './config/websiteCaptureLaunch';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import {
  PAID_UTILITIES,
  SERVICE_COMMAND_CENTRE_ENTITLEMENT,
  SCC_CHECKOUT_SEAM_READY,
  isEntitlementActive,
  startPaidProductCheckout,
} from './UtilitiesPage';

// Card columns live in CSS (not inline) so the responsive breakpoints hold.
// Three across on desktop, folding to two and then one. The grid takes any
// number of products, so the catalogue scales without a layout change.
const PU_CSS = `
  .pu-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 22px;
    align-items: stretch;
  }
  @media (max-width: 1280px) {
    .pu-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 20px;
    }
  }
  @media (max-width: 820px) {
    .pu-grid {
      grid-template-columns: 1fr;
      gap: 18px;
    }
  }
  .pu-card {
    transition: transform 240ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 240ms ease,
                border-color 240ms ease;
  }
  .pu-card:hover {
    transform: translateY(-4px);
  }
  .pu-btn {
    transition: transform 160ms cubic-bezier(0.22,1,0.36,1),
                filter 180ms ease,
                box-shadow 180ms ease;
  }
  .pu-btn:hover {
    filter: brightness(1.12);
    transform: translateY(-2px);
  }
`;

// One compact catalogue card. Status and action come from the real entitlement
// state for Service Command Centre; every other product keeps its real
// pre-release status.
function PaidUtilityCard({ util }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth ? auth.user : null;
  const accent = util.accent;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [informationOpen, setInformationOpen] = useState(false);

  // Content Planner's availability is decided by the SERVER launch gate, not by
  // this page. Fail closed: until the server says purchasable, the card reads
  // Not Available Yet and carries no enabled purchase CTA.
  const cpLaunch = useContentPlannerLaunch();
  const wcsLaunch = useWebsiteCaptureLaunch();

  // Shared consent controller for the SCC purchase, which is a SERVICE contract: in the EU/UK the
  // server asks for an express request to start early, and the acknowledgement wording it returns
  // reflects the service rule rather than an immediate loss of the right to cancel.
  const consentCheckout = useGuardedCheckout();

  let statusLabel = 'Coming Soon';
  let statusTone = '#fbbf24';
  let btnLabel = 'Coming Soon';
  let btnEnabled = false;
  let onClick = null;
  let sccDccAccess = false;

  if (util.kind === 'information-only') {
    statusLabel = util.statusLabel || 'Coming Soon';
    btnLabel = util.ctaLabel || 'View product details';
    btnEnabled = true;
    onClick = () => setInformationOpen((open) => !open);
  } else if (util.launchGate === 'content-planner') {
    statusLabel = cpLaunch.statusLabel;
    btnLabel = cpLaunch.purchasable ? 'Get Content Planner' : cpLaunch.statusLabel;
    statusTone = cpLaunch.purchasable ? '#34d399' : '#fbbf24';
    // No enabled purchase path while pre-launch. When the owner enables sales
    // the CTA is connected deliberately, not by this flag flipping alone.
    btnEnabled = false;
  } else if (util.launchGate === 'website-capture') {
    // Website Capture Suite is an ACTIVE paid product. The server launch gate
    // (GET /v1/website-capture/commerce) decides purchasability; the card stays
    // available either way. The Buy control is a REAL checkout, enabled ONLY when
    // the server reports purchasable, so it cannot start a purchase while sales
    // are disabled, and the moment the owner enables sales it works with no
    // further code change.
    statusLabel = wcsLaunch.statusLabel;
    btnLabel = wcsLaunch.purchasable ? 'Get Website Capture Suite' : wcsLaunch.statusLabel;
    statusTone = wcsLaunch.purchasable ? '#34d399' : '#fbbf24';
    btnEnabled = wcsLaunch.purchasable && !busy;
    onClick = wcsLaunch.purchasable
      ? async () => {
          setError('');
          setBusy(true);
          try {
            const res = await consentCheckout.start(
              () => startWebsiteCaptureCheckout(),
              { purchaseType: 'website_capture', intendedSelector: 'standard' });
            if (res && res.data && res.data.checkoutUrl) {
              window.location.href = res.data.checkoutUrl;
            } else if (res) {
              setError('We could not start checkout. Please try again.');
            }
          } catch (_err) {
            setError('We could not start checkout. Please try again.');
          } finally {
            setBusy(false);
          }
        }
      : null;
  } else if (util.kind === 'scc') {
    const active = isEntitlementActive(user, SERVICE_COMMAND_CENTRE_ENTITLEMENT);
    if (active) {
      statusLabel = 'Active';
      statusTone = '#34d399';
      // SCC launches from the VISINVOKE Desktop Command Centre, not the web
      // dashboard, so no web launch control is shown here.
      sccDccAccess = true;
    } else {
      statusLabel = 'Subscription Required';
      statusTone = '#fbbf24';
      btnLabel = busy ? 'Starting Checkout...' : 'Activate Service Command Centre';
      btnEnabled = SCC_CHECKOUT_SEAM_READY && !busy;
      onClick = SCC_CHECKOUT_SEAM_READY
        ? () => {
            setError('');
            setBusy(true);
            startPaidProductCheckout('scc', {
              onError: (m) => { setError(m); },
              onSettled: () => { setBusy(false); },
              // The server wants the immediate-supply acknowledgement first. Hand the refusal to
              // the shared controller so the customer gets the acknowledgement step, not an error.
              onConsentRequired: (err) => {
                setError('');
                consentCheckout.start(() => Promise.reject(err), { purchaseType: 'service_command_centre' });
              },
            });
          }
        : null;
    }
  } else if (util.ctaRoute) {
    // A paid product whose commercial detail lives on its own dedicated page (e.g. AI Rebuild pricing).
    statusLabel = util.statusLabel || 'Available';
    statusTone = util.accent || '#34d399';
    btnLabel = util.ctaLabel || 'View & Pricing';
    btnEnabled = true;
    onClick = () => { navigate(util.ctaRoute); };
  }

  const cardStyle = {
    ...pu.card,
    background: viGlassFill(accent, accent, accent),
    border: viGlassBorder(accent, 1.5),
    boxShadow: viGlassShadow(accent, accent, accent, false),
  };

  return (
    <div className="pu-card" style={cardStyle} id={util.key} tabIndex={-1}>
      <div style={{ ...pu.topLine, background: 'linear-gradient(90deg, ' + accent + ', ' + hexA(accent, 0.35) + ')' }} />

      {/* L1 - full product title on its own row */}
      <h2 style={pu.title}>{util.title}</h2>

      {/* L2 - code / monogram + descriptor */}
      <div style={pu.identLine}>
        <span style={{ ...pu.codeChip, ...glassSurface(accent, accent, accent) }}>{util.icon}</span>
        <span style={{ ...pu.descriptor, color: accent }}>{util.eyebrow}</span>
      </div>

      {/* L3 - price: amount large, billing unit separated */}
      <div style={pu.priceBlock}>
        <ProductPriceBlock price={util.price} unit={util.billing} note={util.priceNote} />
        {util.launchGate === 'content-planner' && !cpLaunch.purchasable ? (
          <div style={pu.priceNote}>{cpLaunch.customerMessage}</div>
        ) : null}
        {util.launchGate === 'website-capture' && !wcsLaunch.purchasable ? (
          <div style={pu.priceNote}>{wcsLaunch.customerMessage}</div>
        ) : null}
        {util.tiers ? (
          <div style={pu.tierRow}>
            {util.tiers.map((t, i) => (
              <div key={i} style={{ ...pu.tierChip, background: viGlassFill(accent, accent, accent), border: viGlassBorder(accent, 1.5), boxShadow: viGlassShadow(accent, accent, accent, false) }}>
                <span style={pu.tierName}>{t.title.replace('Content Planner ', '')}</span>
                <span style={pu.tierPrice}>{t.price}</span>
                <span style={pu.tierCapacity}>{t.capacity}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* L4 - availability / release state / due, on its own row */}
      <div style={pu.availRow}>
        <AvailabilityBadge state={{ label: statusLabel, tone: statusTone }} />
        {util.dueLabel ? <span style={pu.due}>{util.dueLabel}</span> : null}
      </div>

      {/* Purpose */}
      {util.headline ? <h3 style={pu.headline}>{util.headline}</h3> : null}
      <p style={pu.body}>{util.body}</p>

      {/* Key capabilities */}
      <ul style={pu.features}>
        {util.features.map((f, i) => (
          <li key={i} style={pu.feature}>
            <span style={{ ...pu.dot, background: accent }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {util.featureNote ? <div style={pu.featureNote}>{util.featureNote}</div> : null}
      {util.note ? <div style={pu.note}>{util.note}</div> : null}
      {informationOpen ? (
        <div style={pu.informationPanel} aria-live="polite">
          <div><strong>Demo:</strong> {util.demoPresentation}</div>
          <div><strong>Local-first:</strong> {util.localFirst}</div>
          <div><strong>DCC:</strong> {util.dccWording}</div>
          <div><strong>Status:</strong> {util.statusLabel} · {util.dueLabel}</div>
        </div>
      ) : null}

      {/* Rendered ONLY when the server refuses the checkout pending an acknowledgement. */}
      <GuardedCheckoutConsent
        checkout={consentCheckout}
        onDone={(res) => {
          const url = res && res.data && (res.data.checkout_url || res.data.checkoutUrl);
          if (url) window.location.href = url;
        }}
      />

      {/* Status / access / action */}
      <div style={pu.footer}>
        <div style={pu.metaRow}>
          <span style={pu.access}>{util.access}</span>
        </div>
        {sccDccAccess ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#e6edf5', lineHeight: 1.4 }}>Available in VISINVOKE Desktop Command Centre</div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.62)', lineHeight: 1.5, marginTop: 4 }}>Use DCC on your computer to launch Service Command Centre.</div>
          </div>
        ) : btnEnabled ? (
          <button
            className="pu-btn"
            style={{ ...pu.btn, ...glassSurface(accent, accent, accent) }}
            onClick={onClick}
          >
            {btnLabel}
          </button>
        ) : (
          <button style={pu.btnDisabled} disabled>{btnLabel}</button>
        )}
        {error ? <div style={pu.error}>{error}</div> : null}

        {/* Opens this product's uniform Dashboard detail page. Not a launch,
            download, purchase or run control. */}
        {(util.detailKey || util.moreInfoRoute) ? (
          <MoreInfoPill
            productKey={util.detailKey || util.key}
            route={util.moreInfoRoute}
            accent={accent}
          />
        ) : null}
      </div>
    </div>
  );
}

export default function PaidUtilitiesPage() {
  const navigate = useNavigate();

  useEffect(() => {
    let style = document.getElementById('pu-css');
    if (!style) {
      style = document.createElement('style');
      style.id = 'pu-css';
      document.head.appendChild(style);
    }
    style.textContent = PU_CSS;
    const focusProduct = () => {
      const productId = window.location.hash.replace(/^#/, '');
      if (!productId) return;
      const product = document.getElementById(productId);
      if (!product) return;
      product.scrollIntoView({ behavior: 'smooth', block: 'center' });
      product.focus({ preventScroll: true });
    };
    const timer = window.setTimeout(focusProduct, 0);
    window.addEventListener('hashchange', focusProduct);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('hashchange', focusProduct);
    };
  }, []);

  return (
    <div style={pu.page}>
      <ProductNav />

      {/* Back to Utilities - same pattern as the Free Utilities page */}
      <div className="vi-desktop-wide" style={pu.backWrap}>
        <button
          style={pu.backLink}
          onClick={() => navigate('/dashboard/utilities')}
          onMouseEnter={e => { e.currentTarget.style.color = '#7dd3fc'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#aab8c9'; }}
        >
          {'←'} VISINVOKE Utilities
        </button>
      </div>

      {/* Hero / intro */}
      <div className="vi-desktop-wide" style={pu.intro}>
        <div style={pu.introEyebrow}>VISINVOKE Utilities</div>
        <h1 style={pu.introTitle}>PAID VISINVOKE UTILITIES</h1>
        <p style={pu.introSubtitle}>Professional VISINVOKE utilities for business, productivity and specialist workflows.</p>
        <p style={pu.introBody}>
          These are complete VISINVOKE products, each with its own subscription. They are part of the same VISINVOKE
          ecosystem as your account and the Desktop Command Center, so there is nothing separate to install or manage.
          Browse the catalogue below for what each product does, what it costs and how it is accessed.
        </p>
      </div>

      {/* Catalogue - highest primary monthly price first */}
      <div className="vi-desktop-wide" style={pu.gridWrap}>
        <div className="pu-grid">
          {PAID_UTILITIES.map((util) => (
            <PaidUtilityCard key={util.key} util={util} />
          ))}
        </div>
      </div>
    </div>
  );
}

const pu = {
  page: {
    minHeight: '100vh',
    background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  backWrap: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '22px 40px 0',
    width: '100%',
    boxSizing: 'border-box',
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: '#aab8c9',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    transition: 'color 180ms ease',
  },
  intro: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '14px 40px 0',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  introEyebrow: {
    fontSize: 13,
    fontWeight: 800,
    color: '#7dd3fc',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    margin: '0 0 12px',
  },
  introTitle: {
    fontSize: 40,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.6px',
    lineHeight: 1.1,
    margin: 0,
  },
  introSubtitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#e2e8f0',
    lineHeight: 1.45,
    margin: '12px auto 0',
    maxWidth: 860,
  },
  introBody: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.65,
    margin: '12px auto 0',
    maxWidth: 860,
  },
  gridWrap: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '30px 40px 72px',
    width: '100%',
    boxSizing: 'border-box',
  },

  // ---- catalogue card ----
  card: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    background: 'linear-gradient(160deg, #14223c 0%, #111c30 45%, #0e1626 100%)',
    borderRadius: 16,
    padding: '28px 26px 26px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
  },
  topLine: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  identLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: -6,
  },
  codeChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 34,
    height: 26,
    padding: '0 9px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1,
    flexShrink: 0,
  },
  descriptor: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    lineHeight: 1.35,
    minWidth: 0,
  },
  availRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 13,
  },
  icon: {
    fontSize: 30,
    lineHeight: 1,
    flexShrink: 0,
    color: '#e8eef7',
    fontWeight: 800,
    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    lineHeight: 1.35,
  },
  title: {
    fontSize: 22,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    lineHeight: 1.2,
    margin: '2px 0 0',
  },
  priceBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingBottom: 16,
    borderBottom: '1px solid rgba(255,255,255,0.10)',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 9,
    flexWrap: 'wrap',
  },
  price: {
    fontSize: 28,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  billing: {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
  },
  priceNote: {
    fontSize: 12.5,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.58)',
  },
  tierRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 2,
  },
  tierChip: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: '1 1 140px',
    minWidth: 0,
    borderRadius: 10,
    border: '1px solid',
    background: 'rgba(255,255,255,0.05)',
    padding: '9px 12px',
  },
  tierName: {
    fontSize: 11,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.72)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  tierPrice: {
    fontSize: 17,
    fontWeight: 900,
    color: '#ffffff',
  },
  tierCapacity: {
    fontSize: 11.5,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.62)',
  },
  body: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.86)',
    lineHeight: 1.6,
    margin: 0,
  },
  headline: {
    fontSize: 18,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.35,
    margin: 0,
  },
  features: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '9px 14px',
  },
  feature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 9,
    fontSize: 13,
    color: 'rgba(255,255,255,0.86)',
    lineHeight: 1.4,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 6,
  },
  featureNote: {
    fontSize: 12.5,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.5,
  },
  note: {
    fontSize: 12.5,
    color: 'rgba(255,255,255,0.66)',
    lineHeight: 1.55,
    padding: '11px 13px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
  },
  informationPanel: {
    display: 'grid',
    gap: 8,
    fontSize: 12.5,
    color: 'rgba(255,255,255,0.78)',
    lineHeight: 1.55,
    padding: '12px 14px',
    borderRadius: 10,
    background: 'rgba(96,165,250,0.08)',
    border: '1px solid rgba(96,165,250,0.28)',
  },
  footer: {
    marginTop: 'auto',
    paddingTop: 16,
    borderTop: '1px solid rgba(255,255,255,0.10)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 20,
    border: '1px solid',
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  access: {
    fontSize: 12,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.62)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  due: {
    fontSize: 12,
    fontWeight: 800,
    color: '#fbbf24',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  btn: {
    border: 'none',
    borderRadius: 10,
    padding: '13px 20px',
    fontSize: 14.5,
    fontWeight: 800,
    cursor: 'pointer',
    width: '100%',
    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
  },
  btnDisabled: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    borderRadius: 10,
    padding: '13px 20px',
    fontSize: 14.5,
    fontWeight: 800,
    cursor: 'not-allowed',
    width: '100%',
  },
  error: {
    fontSize: 12,
    fontWeight: 600,
    color: '#fca5a5',
  },
};
